import { useStored } from "../travel/tripStore";
import { BackupBar } from "@/components/BackupBar";
import { lazy, Suspense, useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Download, Plus, RotateCcw, Trash2 } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { safeCsvText } from "../file/csvParse";
import { ConfirmDialog } from "./ConfirmDialog";
import { formatUsd, checkAmount } from "./moneyInput";
import { DEFAULT_EXPENSES, DEFAULT_INCOME, KIND_LABEL, RULE_50_30_20, budgetCsvRows, kindOf, lineErrors, monthlyOf, pctOfIncome, periodOf, summarize, type Kind, type Line, type Period } from "./budgetMath";
import { planBudget, budgetSteps } from "./budgetPlan";
import { reportTable } from "@/lib/calc/report";
import type { TableColumn } from "@/lib/calc/table";

const CalcReport = lazy(() => import("@/components/calc/CalcReport"));

const TOOL_INFO_CONTENT = {
  what: `Tracks monthly income against expenses by category, showing what you spend, what you save and what is left over as you adjust numbers, without needing a dedicated budgeting app.`,
  how: `Add your income sources and expense categories with their amounts, and mark each expense as a need, a want, savings or a debt payment (the required minimum counts as a need). For a yearly bill such as car insurance, choose "per year": a twelfth of it is set aside in your monthly totals. Your income, spending, savings and left-over amount calculate automatically, with a 50/30/20 view and each category's share of income. Amounts accept entries like 1,200 or $400.`,
  tip: `Revisit and adjust the categories every few months rather than setting them once — spending patterns shift, and a budget category that made sense six months ago sometimes doesn't reflect how money is actually being spent now.`,
};

type Update = (id: number, field: keyof Line, value: string) => void;

// Module level on purpose: declared inside the page component it was re-created on every keystroke,
// which remounded the inputs and dropped focus after each character.
const List = ({ items, errors, income, kinds, onUpdate, onRemove, onAdd, addLabel, noun }: {
  items: Line[]; errors: Record<number, string>; income: number | null; kinds: boolean;
  onUpdate: Update; onRemove: (id: number) => void; onAdd: () => void; addLabel: string; noun: string;
}) => (
  <div className="space-y-3">
    {items.map(item => {
      const err = errors[item.id];
      const amt = err ? null : checkAmount(item.amount).value;
      const monthly = err || amt === null ? null : monthlyOf(item);
      const pct = kinds && monthly !== null && income !== null ? pctOfIncome(monthly, income) : null;
      return (
        <div key={item.id}>
          <div className={`grid ${kinds ? "grid-cols-[1fr_96px_84px_36px]" : "grid-cols-[1fr_100px_36px]"} gap-2 items-center`}>
            <Input aria-label={`${noun} name`} value={item.name} onChange={e => onUpdate(item.id, "name", e.target.value)} />
            <Input aria-label={`${item.name || noun} amount`} inputMode="decimal" value={item.amount} aria-invalid={!!err} className={err ? "border-destructive" : ""} onChange={e => onUpdate(item.id, "amount", e.target.value)} />
            {kinds && (
              <select aria-label={`${item.name || noun} category`} className="h-9 rounded-md border bg-background px-1 text-xs" value={kindOf(item)} onChange={e => onUpdate(item.id, "kind", e.target.value)}>
                {(Object.keys(KIND_LABEL) as Kind[]).map(k => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
              </select>
            )}
            <Button type="button" size="icon" variant="ghost" aria-label={`Remove ${item.name || noun}`} onClick={() => onRemove(item.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          {err && <p role="alert" className="text-xs text-destructive mt-1">{err}</p>}
          {kinds && (
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <select aria-label={`${item.name || noun} period`} className="h-7 rounded-md border bg-background px-1 text-xs" value={periodOf(item)} onChange={e => onUpdate(item.id, "period", e.target.value)}>
                <option value="month">per month</option><option value="year">per year</option>
              </select>
              {periodOf(item) === "year" && monthly !== null && <span data-testid="bp-monthly-equivalent">= {formatUsd(monthly, 2)} a month set aside</span>}
              {kindOf(item) === "debt" && <span>Enter the required minimum only.</span>}
            </div>
          )}
          {pct !== null && (
            <div className="mt-1 flex items-center gap-2" aria-label={`${item.name || noun}: ${pct.toFixed(1)}% of income`}>
              <div className="h-1.5 flex-1 rounded bg-muted overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.min(100, pct)}%` }} /></div>
              <span className="text-[11px] tabular-nums text-muted-foreground w-12 text-right">{pct.toFixed(pct < 10 ? 1 : 0)}%</span>
            </div>
          )}
        </div>
      );
    })}
    <Button type="button" size="sm" variant="outline" onClick={onAdd}>
      <Plus className="h-4 w-4 mr-1" /> {addLabel}
    </Button>
  </div>
);

export default function MonthlyBudgetPlanner() {
  const [income, setIncome] = useStored<Line[]>("fdp-MonthlyBudgetPlanner-income-v1", DEFAULT_INCOME);
  const [expenses, setExpenses] = useStored<Line[]>("fdp-MonthlyBudgetPlanner-expenses-v1", DEFAULT_EXPENSES);
  const [nextIncomeId, setNextIncomeId] = useStored("fdp-MonthlyBudgetPlanner-nextIncomeId-v1", 2);
  const [nextExpenseId, setNextExpenseId] = useStored("fdp-MonthlyBudgetPlanner-nextExpenseId-v1", 8);
  const [pending, setPending] = useState<null | "clear" | "reset">(null);
  const headingId = useId();

  const addIncome = () => { setIncome([...income, { id: nextIncomeId, name: "New Income", amount: "0" }]); setNextIncomeId(nextIncomeId + 1); };
  const addExpense = () => { setExpenses([...expenses, { id: nextExpenseId, name: "New Expense", amount: "0", kind: "need" }]); setNextExpenseId(nextExpenseId + 1); };
  const removeIncome = (id: number) => setIncome(income.filter(i => i.id !== id));
  const removeExpense = (id: number) => setExpenses(expenses.filter(e => e.id !== id));
  const updateIncome: Update = (id, field, value) => setIncome(income.map(i => (i.id === id ? { ...i, [field]: value } : i)));
  const updateExpense: Update = (id, field, value) => setExpenses(expenses.map(e => (e.id === id ? { ...e, [field]: value } : e)));

  const incomeErrors = useMemo(() => lineErrors(income), [income]);
  const expenseErrors = useMemo(() => lineErrors(expenses), [expenses]);
  const sum = useMemo(() => summarize(income, expenses), [income, expenses]);
  const hasErrors = Object.keys(incomeErrors).length + Object.keys(expenseErrors).length > 0;

  // Reading is free; copying, printing or saving the result (including the CSV below) uses one trial use per visit.
  const { gate } = useLogToolUsage(!!sum);

  type LineRow = { key: string; type: string; name: string; category: string; entered: number; per: string; monthly: number };
  const tableRows: LineRow[] = sum ? [
    ...income.map((l) => ({ key: `i${l.id}`, type: "Income", name: l.name, category: "", entered: checkAmount(l.amount).value ?? 0, per: "month", monthly: checkAmount(l.amount).value ?? 0 })),
    ...expenses.map((l) => ({ key: `e${l.id}`, type: "Expense", name: l.name, category: KIND_LABEL[kindOf(l)], entered: checkAmount(l.amount).value ?? 0, per: periodOf(l) as Period, monthly: monthlyOf(l) })),
  ] : [];
  const lineColumns: TableColumn<LineRow>[] = [
    { key: "type", header: "Type", get: (r) => r.type }, { key: "name", header: "Name", get: (r) => r.name }, { key: "category", header: "Category", get: (r) => r.category },
    { key: "entered", header: "Entered", get: (r) => r.entered, format: (r) => formatUsd(r.entered, 2), align: "right" }, { key: "per", header: "Per", get: (r) => r.per },
    { key: "monthly", header: "Monthly amount", get: (r) => Math.round(r.monthly * 100) / 100, format: (r) => formatUsd(r.monthly, 2), align: "right" },
  ];
  const steps = useMemo(() => (sum ? budgetSteps(planBudget(sum.income, expenses.map((l) => ({ name: l.name, amount: checkAmount(l.amount).value ?? 0, kind: kindOf(l), period: periodOf(l) }))), (n) => formatUsd(n, 2)) : []), [sum, expenses]);

  const applyWithUndo = (kind: "clear" | "reset") => {
    const prev = { income, expenses, ni: nextIncomeId, ne: nextExpenseId };
    if (kind === "clear") { setIncome([]); setExpenses([]); } else { setIncome(DEFAULT_INCOME); setExpenses(DEFAULT_EXPENSES); setNextIncomeId(2); setNextExpenseId(8); }
    setPending(null);
    toast(kind === "clear" ? "All rows cleared" : "Reset to the sample budget", {
      duration: 10000,
      action: { label: "Undo", onClick: () => { setIncome(prev.income); setExpenses(prev.expenses); setNextIncomeId(prev.ni); setNextExpenseId(prev.ne); } },
    });
  };

  const exportCsv = () => {
    if (!sum) return;
    downloadBlob(new Blob([safeCsvText(budgetCsvRows(income, expenses, sum))], { type: "text/csv;charset=utf-8" }), "monthly-budget.csv");
  };

  const incomeForPct = sum && sum.income > 0 ? sum.income : null;
  const dash = "—";

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <Label className="mb-3 block text-sm font-semibold">Income</Label>
          <List items={income} errors={incomeErrors} income={null} kinds={false} onUpdate={updateIncome} onRemove={removeIncome} onAdd={addIncome} addLabel="Add Income" noun="Income" />
        </div>
        <div>
          <Label className="mb-3 block text-sm font-semibold">Expenses &amp; savings</Label>
          <List items={expenses} errors={expenseErrors} income={incomeForPct} kinds onUpdate={updateExpense} onRemove={removeExpense} onAdd={addExpense} addLabel="Add Expense" noun="Expense" />
        </div>
      </div>

      {hasErrors && <p role="alert" className="text-sm text-destructive mt-6">Fix the red amounts to see your totals. Nothing is counted as 0 until it is a number.</p>}

      <div data-result>
        <div className="grid grid-cols-3 gap-4 mt-6">
          <h2 id={headingId} className="sr-only">Monthly totals</h2>
          <Card className="p-4 text-center">
            <p className="text-xl font-bold text-green-600">{sum ? formatUsd(sum.income) : dash}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Income</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-xl font-bold text-red-600">{sum ? formatUsd(sum.spending) : dash}</p>
            <p className="text-xs text-muted-foreground mt-1">Expenses (not savings)</p>
          </Card>
          <Card className="p-4 text-center">
            <p className={`text-xl font-bold ${sum && sum.leftOver < 0 ? "text-red-600" : "text-green-600"}`}>{sum ? formatUsd(sum.leftOver) : dash}</p>
            <p className="text-xs text-muted-foreground mt-1">{sum && sum.leftOver < 0 ? "Shortfall" : "Left over"}</p>
          </Card>
        </div>
        {sum && (
          <p className="text-sm text-center mt-3">
            Saved {formatUsd(sum.saved)} · <span className={sum.leftOver < 0 ? "text-red-600" : ""}>Left over {formatUsd(sum.leftOver)}</span>
          </p>
        )}
        {sum && (sum.debt > 0 || sum.yearlySetAside > 0) && (
          <p className="text-xs text-center text-muted-foreground mt-1" data-testid="bp-extras-line">
            {sum.debt > 0 && <>Debt payments {formatUsd(sum.debt, 2)} (counted in needs)</>}{sum.debt > 0 && sum.yearlySetAside > 0 && " · "}{sum.yearlySetAside > 0 && <>Set aside for yearly bills {formatUsd(sum.yearlySetAside, 2)} a month</>}
          </p>
        )}

        {sum && (
          <section className="mt-6 rounded-lg border p-4" aria-label="50/30/20 view">
            <h2 className="text-sm font-semibold">50/30/20 view</h2>
            <p className="text-xs text-muted-foreground mb-3">A common rule of thumb: about 50% of income on needs, 30% on wants and 20% on savings.</p>
            {incomeForPct === null ? (
              <p className="text-sm text-muted-foreground">Add some income to see shares.</p>
            ) : (
              <div className="space-y-3">
                {RULE_50_30_20.map(r => {
                  const amount = r.kind === "need" ? sum.needs : r.kind === "want" ? sum.wants : sum.saved;
                  const p = pctOfIncome(amount, sum.income) ?? 0;
                  const over = r.kind === "savings" ? p < r.target : p > r.target;
                  return (
                    <div key={r.kind}>
                      <div className="flex justify-between text-xs mb-1">
                        <span>{r.label} <span className="text-muted-foreground">(target {r.target}%)</span></span>
                        <span className={`tabular-nums ${over ? "text-red-600" : ""}`}>{p.toFixed(1)}% · {formatUsd(amount)}</span>
                      </div>
                      <div className="relative h-2 rounded bg-muted overflow-hidden">
                        <div className={`h-full ${over ? "bg-red-500" : "bg-primary"}`} style={{ width: `${Math.max(0, Math.min(100, p))}%` }} />
                        <div className="absolute inset-y-0 w-0.5 bg-foreground/60" style={{ left: `${r.target}%` }} aria-hidden="true" />
                      </div>
                    </div>
                  );
                })}
                <p className="text-xs text-muted-foreground">Unallocated: {formatUsd(sum.leftOver)} ({(pctOfIncome(sum.leftOver, sum.income) ?? 0).toFixed(1)}% of income).</p>
              </div>
            )}
          </section>
        )}
      </div>

      {sum && (
        <Suspense fallback={<p className="text-xs text-muted-foreground" role="status">Loading working and export...</p>}>
          <CalcReport
            title="Monthly budget" tool="monthly-budget-planner" gate={gate} filename="monthly-budget-report.csv" steps={steps}
            summary={`Income ${formatUsd(sum.income)}, needs ${formatUsd(sum.needs)}, wants ${formatUsd(sum.wants)}, saved ${formatUsd(sum.saved)}, left over ${formatUsd(sum.leftOver)}`}
            notes={["A yearly amount counts as a twelfth each month. A debt payment counts in needs. Illustration, not financial advice."]}
            tables={[reportTable({ caption: "Budget lines as monthly amounts", heading: "Budget lines", rows: tableRows, columns: lineColumns, rowKey: (r) => r.key })]}
          />
        </Suspense>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Button type="button" size="sm" variant="outline" onClick={exportCsv} disabled={!sum}><Download className="h-3.5 w-3.5 mr-1" />Export CSV</Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setPending("reset")}><RotateCcw className="h-3.5 w-3.5 mr-1" />Reset to sample</Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setPending("clear")}><Trash2 className="h-3.5 w-3.5 mr-1" />Clear all rows</Button>
      </div>
      <ConfirmDialog
        open={pending !== null}
        title={pending === "clear" ? "Clear all rows?" : "Reset to the sample budget?"}
        body={pending === "clear" ? "This removes every income and expense row so you can start from a blank budget. You can undo it right after." : "This replaces your rows with the sample budget. You can undo it right after."}
        confirmLabel={pending === "clear" ? "Clear all rows" : "Reset to sample"}
        onConfirm={() => pending && applyWithUndo(pending)}
        onCancel={() => setPending(null)}
      />
      <BackupBar tool="monthly-budget-planner" prefixes={["fdp-MonthlyBudgetPlanner-"]} session hideClear />
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
